import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, X, Heart, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

const TRUSTPILOT_URL = "https://se.trustpilot.com/review/snusparty.se";
const STORAGE_KEY = "vapebolag_review_requested";
const CHECKOUT_FLAG = "vapebolag_started_checkout";

export const ReviewRequestPopup = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Check if user has started checkout and returned to site
    const hasStartedCheckout = sessionStorage.getItem(CHECKOUT_FLAG);
    const hasAlreadyRequested = localStorage.getItem(STORAGE_KEY);
    
    // Also check referrer for Shopify thank you page
    const fromShopifyCheckout = document.referrer.includes('myshopify.com') || 
                                 document.referrer.includes('checkout') ||
                                 document.referrer.includes('thank_you');

    // Show popup if they came back from checkout and haven't been asked before
    if ((hasStartedCheckout || fromShopifyCheckout) && !hasAlreadyRequested) {
      // Small delay before showing popup
      const timer = setTimeout(() => {
        setIsOpen(true);
        // Mark as requested so we don't ask again
        localStorage.setItem(STORAGE_KEY, Date.now().toString());
        // Clear checkout flag
        sessionStorage.removeItem(CHECKOUT_FLAG);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleReviewClick = () => {
    window.open(TRUSTPILOT_URL, '_blank', 'noopener,noreferrer');
    setIsOpen(false);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={handleClose}
          />
          
          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed inset-x-4 top-1/2 -translate-y-1/2 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 z-50 max-w-md w-full"
          >
            <div className="glass-effect rounded-3xl border-glow p-6 md:p-8 relative overflow-hidden">
              {/* Close button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors"
                aria-label="Stäng"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>

              {/* Decorative background */}
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-primary/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-cta/20 rounded-full blur-3xl" />

              {/* Content */}
              <div className="relative text-center">
                {/* Icon */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring" }}
                  className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#00b67a] to-[#00a067] flex items-center justify-center"
                >
                  <Heart className="w-10 h-10 text-white" fill="white" />
                </motion.div>

                {/* Title */}
                <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">
                  Tack för ditt köp! 💚
                </h2>

                {/* Description */}
                <p className="text-muted-foreground mb-6">
                  Vi hoppas du älskar din beställning! Din åsikt betyder allt för oss – 
                  lämna gärna en recension på Trustpilot.
                </p>

                {/* Stars */}
                <div className="flex justify-center gap-1 mb-6">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <motion.div
                      key={star}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 + star * 0.1 }}
                    >
                      <Star 
                        className="w-8 h-8 text-[#00b67a]" 
                        fill="#00b67a"
                      />
                    </motion.div>
                  ))}
                </div>

                {/* CTA Button */}
                <Button
                  onClick={handleReviewClick}
                  className="w-full py-6 text-lg font-bold rounded-xl bg-[#00b67a] hover:bg-[#00a067] text-white gap-2"
                >
                  Lämna en recension
                  <ExternalLink className="w-5 h-5" />
                </Button>

                {/* Skip link */}
                <button
                  onClick={handleClose}
                  className="mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Kanske senare
                </button>

                {/* Trustpilot branding */}
                <div className="mt-6 pt-4 border-t border-border/50">
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <svg className="w-5 h-5 text-[#00b67a]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                    </svg>
                    <span>Powered by Trustpilot</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Export function to mark checkout started (call this when user clicks checkout)
export const markCheckoutStarted = () => {
  sessionStorage.setItem(CHECKOUT_FLAG, "true");
};
